
/*
 * BOCHAN - Cart Drawer
 *
 * Preserves:
 * - Cart drawer open/close
 * - Cart icon interaction
 * - AJAX cart rendering
 * - Upsell product addition
 * - Before Checkout upsell screen
 * - Back to cart
 * - Checkout navigation
 *
 * Improvements:
 * - Preserve open drawer during refresh
 * - Never replace the entire cart-drawer element
 * - Avoid duplicate overlay listeners
 * - Keep cart icon functional after AJAX updates
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.openTimer = null;
    this.focusTrapTimer = null;

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') {
        this.close();
      }
    });

    this.addEventListener('click', (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) {
        this.close();
      }
    });

    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    // Delegation keeps the icon working after AJAX updates.
    if (window.__bochanCartIconHandlerInstalled) return;

    window.__bochanCartIconHandlerInstalled = true;

    document.addEventListener('click', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');

      if (!cartLink) return;

      const drawer = document.querySelector('cart-drawer');

      if (!drawer) return;

      event.preventDefault();
      drawer.open(cartLink);
    });

    document.addEventListener('keydown', (event) => {
      const cartLink = event.target.closest('#cart-icon-bubble');

      if (!cartLink) return;

      if (event.code === 'Space' || event.code === 'Enter') {
        event.preventDefault();

        const drawer = document.querySelector('cart-drawer');

        if (drawer) drawer.open(cartLink);
      }
    });

    const cartLink = document.querySelector('#cart-icon-bubble');

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }
  }

  open(triggeredBy) {
    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    }

    clearTimeout(this.openTimer);

    const cartDrawerNote = this.querySelector(
      '[id^="Details-"] summary'
    );

    if (
      cartDrawerNote &&
      !cartDrawerNote.hasAttribute('role')
    ) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.classList.add('animate');

    this.openTimer = setTimeout(() => {
      this.classList.add('active');
    }, 0);

    document.body.classList.add('overflow-hidden');

    this.addEventListener(
      'transitionend',
      () => {
        if (!this.classList.contains('active')) return;

        const focusContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : this.querySelector('#CartDrawer');

        const focusElement =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close');

        if (
          focusContainer &&
          focusElement &&
          typeof trapFocus === 'function'
        ) {
          trapFocus(focusContainer, focusElement);
        }
      },
      { once: true }
    );
  }

  close() {
    clearTimeout(this.openTimer);

    this.classList.remove('active');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (
      cartDrawerNote.nextElementSibling &&
      cartDrawerNote.nextElementSibling.getAttribute('id')
    ) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      const details = event.currentTarget.closest('details');

      if (details) {
        event.currentTarget.setAttribute(
          'aria-expanded',
          !details.hasAttribute('open')
        );
      }
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener(
        'keyup',
        onKeyUpEscape
      );
    }
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return;

    // Remember the current state before replacing content.
    const wasOpen = this.classList.contains('active');

    const drawerInner = this.querySelector('.drawer__inner');

    if (drawerInner) {
      drawerInner.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = parsedState.sections[section.id];

      if (!sectionElement || !sectionHTML) return;

      const updatedHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );

      if (updatedHTML !== null) {
        sectionElement.innerHTML = updatedHTML;
      }
    });

    // Keep the drawer open only if it was already open.
    if (wasOpen) {
      this.classList.add('active', 'animate');
      document.body.classList.add('overflow-hidden');
    }

    const cartLink = document.querySelector('#cart-icon-bubble');

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return null;

    const doc = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const element = doc.querySelector(selector);

    return element ? element.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;

    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}


/*
 * CART DRAWER ITEMS
 */

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}


/*
 * HELPERS
 */

function bochanGetCartDrawer() {
  return document.querySelector('cart-drawer');
}

async function bochanFetchCartDrawerHTML() {
  const baseUrl =
    window.routes && window.routes.cart_url
      ? window.routes.cart_url
      : '/cart';

  const separator = baseUrl.includes('?') ? '&' : '?';

  const response = await fetch(
    `${baseUrl}${separator}section_id=cart-drawer`,
    { cache: 'no-store' }
  );

  if (!response.ok) {
    throw new Error(
      `Cart drawer refresh failed (${response.status})`
    );
  }

  const html = await response.text();

  return new DOMParser().parseFromString(
    html,
    'text/html'
  );
}


/*
 * REFRESH DRAWER WITHOUT REPLACING CUSTOM ELEMENT
 */

let bochanDrawerRefreshVersion = 0;

async function bochanRefreshDrawer() {
  const version = ++bochanDrawerRefreshVersion;

  const drawer = bochanGetCartDrawer();

  if (!drawer) return;

  const doc = await bochanFetchCartDrawerHTML();

  if (version !== bochanDrawerRefreshVersion) return;

  const currentDrawer = bochanGetCartDrawer();

  if (!currentDrawer) return;

  const wasOpen = currentDrawer.classList.contains('active');

  const newDrawer = doc.querySelector('cart-drawer');

  if (!newDrawer) return;

  const newInner = newDrawer.querySelector('.drawer__inner');
  const oldInner = currentDrawer.querySelector('.drawer__inner');

  if (newInner && oldInner) {
    // Replace only inner content, not the custom element.
    oldInner.innerHTML = newInner.innerHTML;
  }

  currentDrawer.classList.toggle(
    'is-empty',
    newDrawer.classList.contains('is-empty')
  );

  if (wasOpen) {
    currentDrawer.classList.add('active', 'animate');
    document.body.classList.add('overflow-hidden');
  }
}


/*
 * UPSELL PRODUCT ADDITION
 */

let bochanUpsellBusy = false;

document.body.addEventListener('click', async (event) => {
  const button = event.target.closest('.add-upsell');

  if (!button) return;

  event.preventDefault();

  if (bochanUpsellBusy) return;

  const variantId = button.dataset.variantId;

  if (!variantId) {
    console.error('Upsell variant ID is missing.');
    return;
  }

  bochanUpsellBusy = true;

  const originalHTML = button.innerHTML;

  button.disabled = true;

  button.innerHTML = `
    <div class="loading__spinner">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        class="spinner"
        viewBox="0 0 66 66"
      >
        <circle
          stroke-width="6"
          cx="33"
          cy="33"
          r="30"
          fill="none"
          class="path"
        ></circle>
      </svg>
    </div>
  `;

  try {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        id: variantId,
        quantity: 1
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));

      throw new Error(
        errorData.description ||
        errorData.message ||
        'Unable to add upsell product.'
      );
    }

    await bochanRefreshDrawer();

  } catch (error) {
    console.error('Error adding upsell:', error);

    if (button.isConnected) {
      button.innerHTML = originalHTML;
    }

  } finally {
    if (button.isConnected) {
      button.disabled = false;
    }

    bochanUpsellBusy = false;
  }
});


/*
 * BEFORE CHECKOUT / UPSELL SCREEN / BACK BUTTON
 */

document.body.addEventListener('click', (event) => {
  const target = event.target.closest(
    '#CartDrawer_Before_Checkout, #cartdrawer_back'
  );

  if (!target) return;

  const cartDrawer = bochanGetCartDrawer();

  if (!cartDrawer) return;

  const upsellWrapper = cartDrawer.querySelector(
    '.upsell-products-wrapper'
  );

  const drawerHeader = cartDrawer.querySelector(
    '.drawer__header-heading'
  );

  const cartDrawerItems = cartDrawer.querySelector(
    'cart-drawer-items'
  );

  const checkoutBtn = cartDrawer.querySelector(
    '#CartDrawer-Checkout'
  );

  const beforeCheckoutBtn = cartDrawer.querySelector(
    '#CartDrawer_Before_Checkout'
  );

  const backBtn = cartDrawer.querySelector(
    '#cartdrawer_back'
  );

  // Back to main cart
  if (target.id === 'cartdrawer_back') {
    event.preventDefault();

    if (upsellWrapper) {
      upsellWrapper.style.display = 'none';
    }

    if (drawerHeader) {
      drawerHeader.style.display = 'flex';
    }

    if (cartDrawerItems) {
      cartDrawerItems.style.display = 'block';
    }

    if (checkoutBtn) {
      checkoutBtn.style.display = 'none';
    }

    if (beforeCheckoutBtn) {
      beforeCheckoutBtn.style.display = 'block';
    }

    if (backBtn) {
      backBtn.style.display = 'none';
    }

    return;
  }

  // Before Checkout
  const upsellProducts = cartDrawer.querySelectorAll(
    '.upsell-product[data-product-id]'
  );

  const cartItems = cartDrawer.querySelectorAll(
    '.cart-item[data-product-id]'
  );

  const upsellIds = Array.from(upsellProducts).map(
    (element) => Number(element.dataset.productId)
  );

  const cartIds = Array.from(cartItems).map(
    (element) => Number(element.dataset.productId)
  );

  // No upsells available: proceed directly to checkout.
  if (upsellIds.length === 0) {
    window.location.href = '/checkout';
    return;
  }

  // All upsells already in cart: proceed to checkout.
  const allInCart = upsellIds.every((id) =>
    cartIds.includes(id)
  );

  if (allInCart) {
    window.location.href = '/checkout';
    return;
  }

  // Otherwise show upsell screen.
  event.preventDefault();

  if (upsellWrapper) {
    upsellWrapper.style.display = 'block';
  }

  if (drawerHeader) {
    drawerHeader.style.display = 'none';
  }

  if (cartDrawerItems) {
    cartDrawerItems.style.display = 'none';
  }

  if (checkoutBtn) {
    checkoutBtn.style.display = 'block';
  }

  if (beforeCheckoutBtn) {
    beforeCheckoutBtn.style.display = 'none';
  }

  if (backBtn) {
    backBtn.style.display = 'block';
  }
});
